import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { ArrowRight } from '@/components/Icons/ArrowRight';
import { Camera } from '@/components/Icons/Camera';
import { Paperclip } from '@/components/Icons/Paperclip';
import { Snackbar } from '@/components/Snackbar';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useGallery } from '@/hooks/useGallery';
import { useGalleryImage } from '@/hooks/useGalleryImage';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNews } from '@/hooks/useNews';
import { Layout } from '@/layouts/Layout';
import { AttachmentCard } from '@/components/AttachmentCard';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { useImageEdgeBackground } from '@/pages/DetailedNews/hooks/useImageEdgeBackground';
import { GalleryLightbox } from '@/pages/DetailedNews/GalleryLightbox';
import { GalleryPreviewModal } from '@/pages/DetailedNews/GalleryPreviewModal';
import {
  GALLERY_GRID_THUMBNAIL_SIZE,
  GalleryThumbnail,
} from '@/pages/DetailedNews/GalleryThumbnail';
import { NewsCard } from '@/pages/Home/NewsCard';
import { ROUTE_HOME } from '@/router/routes';
import { Anchor } from '@/types/anchors';
import type { Attachment } from '@/types/attachment';
import { NewsType, type NewsItem } from '@/types/news';
import { formatDate } from '@/utils/date';
import { goToPreviousPage } from '@/utils/global';
import { sanitizeRichTextHtml } from '@/utils/html';
import { scrollToElement, scrollToTop } from '@/utils/scroll';

interface LocationState {
  news?: NewsItem;
}

const DEFAULT_TYPE: NewsType = NewsType.SystemInfo;
const DESKTOP_GALLERY_PREVIEW_LIMIT = 5;
const MOBILE_GALLERY_PREVIEW_LIMIT = 4;
const HERO_IMAGE_SIZE = 700;
const HERO_MIN_HEIGHT = 100;
const HERO_MAX_HEIGHT = 180;

export const DetailedNews: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation() as { state: LocationState | null };
  const news = state?.news;
  const titleImageUrl = useGalleryImage(news?.titleImageId, HERO_IMAGE_SIZE);
  const isMobile = useIsMobile();
  const { data: allNews } = useNews();
  const { data: gallery } = useGallery(news?.galleryId);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewAnimated, setIsPreviewAnimated] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [isHeroOpen, setIsHeroOpen] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const {
    boxRef: heroBoxRef,
    background: heroBackground,
    height: heroImageHeight,
    handleImageLoad: handleHeroImageLoad,
  } = useImageEdgeBackground({
    imageUrl: titleImageUrl,
    minHeight: HERO_MIN_HEIGHT,
    maxHeight: HERO_MAX_HEIGHT,
  });

  const isPreviewVisible = isPreviewOpen && lightboxIndex === null;
  const galleryPreviewLimit = isMobile
    ? MOBILE_GALLERY_PREVIEW_LIMIT
    : DESKTOP_GALLERY_PREVIEW_LIMIT;

  const openGalleryPreview = () => {
    setIsPreviewAnimated(true);
    setIsPreviewOpen(true);
  };

  const openGalleryImage = (index: number, isMoreTile: boolean) => {
    if (isMoreTile) {
      openGalleryPreview();
      return;
    }

    setLightboxIndex(index);
  };

  const returnToGalleryPreview = () => {
    setIsPreviewAnimated(false);
    setLightboxIndex(null);
  };

  const closeGallery = () => {
    setLightboxIndex(null);
    setIsPreviewOpen(false);
  };

  const getDownloadedMessage = (filename: string) =>
    filename.toLowerCase().endsWith('.pdf')
      ? t('home.newsDetail.documentDownloadedSnackbar')
      : t('home.newsDetail.imageDownloadedSnackbar');

  useEffect(() => {
    scrollToTop(topRef.current);
  }, [news?.title, news?.lastModificationDate]);

  const otherNews =
    allNews?.filter(
      ({ title, lastModificationDate }) =>
        title !== news?.title ||
        lastModificationDate !== news?.lastModificationDate,
    ) ?? [];

  if (!news) {
    return <Navigate to={ROUTE_HOME} replace />;
  }

  const hasAttachments = news.attachments.length > 0;
  const hasGallery = !!news.galleryId;
  const hiddenGalleryImagesCount =
    (gallery?.images.length ?? 0) - galleryPreviewLimit;
  const hasHiddenGalleryImages = hiddenGalleryImagesCount > 0;

  const chips = (
    <>
      {news.isImportant && (
        <span className="rounded-2xl bg-warning px-3 py-1 text-caption-13 md:text-subhead-14 text-accent-foreground">
          {t('home.news.importantLabel')}
        </span>
      )}
      <span className="rounded-2xl border border-transparent bg-constant-black px-3 py-1 text-caption-13 md:text-subhead-14 text-white contrast:border-border dark:bg-white dark:text-constant-black">
        {t(`home.news.types.${news.type ?? DEFAULT_TYPE}`)}
      </span>
    </>
  );
  const date = (
    <div className="flex items-center gap-1.5 text-body-13 text-subtle-foreground md:text-body-14">
      {news.isNew && <span className="size-1.5 rounded-full bg-accent" />}
      <span>{formatDate(news.lastModificationDate)}</span>
    </div>
  );
  const mediaChips = (
    <>
      {hasAttachments && (
        <button
          type="button"
          onClick={() => scrollToElement(Anchor.Attachments)}
          className="inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-[40px] border border-transparent bg-info-surface px-2 py-1 text-body-13 text-foreground contrast:border-border"
        >
          <Paperclip className="size-4 text-primary" />
          {t('home.newsDetail.attachmentsTitle')}
        </button>
      )}
      {hasGallery && (
        <button
          type="button"
          onClick={openGalleryPreview}
          className="inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-[40px] border border-transparent bg-info-surface px-2 py-1 text-body-13 text-foreground contrast:border-border"
        >
          <Camera className="size-4 text-primary" />
          {t('home.newsDetail.galleryTitle')}
        </button>
      )}
    </>
  );

  return (
    <Layout hideMobileHeader hideMobileNavbar>
      <div className="relative flex min-h-0 w-full flex-1 flex-col">
        {news.titleImageId && (
          <div
            ref={heroBoxRef}
            className="absolute inset-x-0 top-0 h-[210px] overflow-hidden md:hidden"
          >
            {titleImageUrl && (
              <button
                type="button"
                onClick={() => setIsHeroOpen(true)}
                style={{ background: heroBackground }}
                className={`relative block size-full cursor-pointer ${heroBackground ? 'animate-fade-in' : 'opacity-0'}`}
              >
                <img
                  src={titleImageUrl}
                  alt=""
                  aria-hidden
                  draggable={false}
                  className="absolute -top-30 -left-30 h-[calc(100%+240px)] w-[calc(100%+240px)] max-w-none object-cover blur-3xl brightness-125 select-none"
                />
                <span
                  style={{ background: heroBackground }}
                  className="absolute inset-0 opacity-60 brightness-125"
                />
                <img
                  src={titleImageUrl}
                  alt=""
                  draggable={false}
                  onLoad={handleHeroImageLoad}
                  style={{ height: heroImageHeight }}
                  className="absolute inset-x-0 top-0 w-full object-contain transition-[height] duration-300 select-none"
                />
              </button>
            )}
            {!heroBackground && (
              <span className="absolute inset-x-0 top-0 flex h-[100px] items-center justify-center">
                <PageSpinner />
              </span>
            )}
          </div>
        )}

        {news.titleImageId && (
          <button
            type="button"
            onClick={goToPreviousPage}
            aria-label={t('global.back')}
            className="absolute top-[calc(16px+env(safe-area-inset-top))] left-4 z-20 flex size-6 cursor-pointer items-center justify-center text-white drop-shadow-[0_1px_2px_rgb(0_0_0/0.25)] md:hidden"
          >
            <ArrowLeft />
          </button>
        )}

        <div
          ref={topRef}
          className={`scrollbar-hidden relative flex w-full flex-1 flex-col max-md:gap-0 md:static md:gap-6 md:overflow-visible md:rounded-none md:px-4 md:py-6 ${news.titleImageId ? 'max-md:absolute max-md:inset-x-0 max-md:top-[100px] max-md:bottom-0 max-md:overflow-y-auto max-md:overscroll-y-none max-md:rounded-t-2xl' : ''}`}
        >
          <article
            key={`${news.lastModificationDate}-${news.title}`}
            className="flex flex-col animate-fade-in md:gap-6 md:rounded-2xl md:bg-card md:p-6 md:shadow-card"
          >
            <div className="hidden flex-col gap-2 md:flex">
              <div className="flex items-start gap-4">
                <div className="flex flex-1 items-start gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousPage}
                    aria-label={t('global.back')}
                    className="flex h-7 w-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
                  >
                    <ArrowLeft />
                  </button>
                  <h1 className="flex-1 text-headline-20 text-foreground">
                    {news.title}
                  </h1>
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-2">
                  {chips}
                </div>
              </div>
              {date}
            </div>

            {news.titleImageId && (
              <div className="relative hidden w-full overflow-hidden rounded-xl bg-card md:block md:h-[400px] md:shadow-card">
                {titleImageUrl && (
                  <button
                    type="button"
                    onClick={() => setIsHeroOpen(true)}
                    className="block size-full cursor-pointer"
                  >
                    <img
                      src={titleImageUrl}
                      alt=""
                      draggable={false}
                      className="size-full object-contain select-none"
                    />
                  </button>
                )}
                {!titleImageUrl && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <PageSpinner />
                  </span>
                )}
                {(hasAttachments || hasGallery) && (
                  <div className="absolute top-3 right-3 flex flex-wrap justify-end gap-2">
                    {mediaChips}
                  </div>
                )}
              </div>
            )}

            {news.titleImageId && (
              <button
                type="button"
                tabIndex={-1}
                aria-hidden
                onClick={() => setIsHeroOpen(true)}
                style={{ height: heroImageHeight - HERO_MIN_HEIGHT }}
                className="shrink-0 cursor-pointer transition-[height] duration-300 md:hidden"
              />
            )}

            <div className="relative z-10 flex flex-col gap-5 rounded-t-2xl bg-background px-4 pt-6 pb-8 md:gap-6 md:rounded-none md:bg-transparent md:p-0">
              {!news.titleImageId && (
                <div className="flex items-start justify-between gap-4 md:hidden">
                  <button
                    type="button"
                    onClick={goToPreviousPage}
                    aria-label={t('global.back')}
                    className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
                  >
                    <ArrowLeft />
                  </button>
                  <div className="flex flex-wrap justify-end gap-2">
                    {mediaChips}
                  </div>
                </div>
              )}

              {news.titleImageId && (hasAttachments || hasGallery) && (
                <div className="absolute right-4 bottom-full mb-6 flex flex-wrap justify-end gap-2 md:hidden">
                  {mediaChips}
                </div>
              )}

              <div className="flex flex-col gap-3 md:hidden">
                <div className="flex flex-wrap items-center gap-2">{chips}</div>
                <div className="flex flex-col gap-2">
                  <h1 className="text-title-24 text-foreground">
                    {news.title}
                  </h1>
                  {date}
                </div>
              </div>

              <div
                className="rich-text text-body-14 text-foreground"
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichTextHtml(news.body),
                }}
              />

              {!!gallery?.images.length && (
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-4">
                      <h2 className="text-callout-16 text-muted-foreground">
                        {t('home.newsDetail.galleryTitle')}
                      </h2>
                      {hasHiddenGalleryImages && (
                        <button
                          type="button"
                          onClick={openGalleryPreview}
                          className="flex h-6 cursor-pointer items-center gap-2 text-body-13 font-semibold text-primary md:hidden"
                        >
                          {t('home.newsDetail.allImagesLabel')}
                          <ArrowRight className="size-4" />
                        </button>
                      )}
                    </div>
                    {gallery.title && (
                      <p className="text-body-14 text-subtle-foreground">
                        {gallery.title}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 md:flex md:flex-wrap">
                    {gallery.images
                      .slice(0, galleryPreviewLimit)
                      .map((image, index) => {
                        const isLast = index === galleryPreviewLimit - 1;
                        const isMoreTile = isLast && hasHiddenGalleryImages;
                        const overlayText = isMoreTile
                          ? `${hiddenGalleryImagesCount}+`
                          : undefined;

                        return (
                          <GalleryThumbnail
                            key={image.id}
                            size={GALLERY_GRID_THUMBNAIL_SIZE}
                            className="aspect-[5/4] w-full md:aspect-auto md:h-[124px] md:w-[140px] md:shrink-0"
                            image={image}
                            overlayText={overlayText}
                            onClick={() => openGalleryImage(index, isMoreTile)}
                          />
                        );
                      })}
                  </div>
                  {hasHiddenGalleryImages && (
                    <button
                      type="button"
                      onClick={openGalleryPreview}
                      className="hidden h-9 cursor-pointer items-center gap-2 self-start text-subhead-14 text-primary md:flex"
                    >
                      {t('home.newsDetail.allImagesLabel')}
                      <ArrowRight className="size-4" />
                    </button>
                  )}
                </section>
              )}

              {hasAttachments && (
                <section
                  id={Anchor.Attachments}
                  className="flex flex-col gap-3"
                >
                  <h2 className="text-callout-16 text-muted-foreground">
                    {t('home.newsDetail.attachmentsTitle')}
                  </h2>
                  <div className="flex flex-wrap gap-5">
                    {news.attachments.map((attachment) => (
                      <AttachmentCard
                        key={attachment.id}
                        attachment={attachment}
                        onPreview={setPreviewAttachment}
                        onDownloaded={() =>
                          setSnackbarMessage(
                            t('home.newsDetail.documentDownloadedSnackbar'),
                          )
                        }
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>

          {gallery && isPreviewVisible && (
            <GalleryPreviewModal
              isAnimated={isPreviewAnimated}
              gallery={gallery}
              onSelect={setLightboxIndex}
              onClose={closeGallery}
            />
          )}

          {gallery && lightboxIndex !== null && (
            <GalleryLightbox
              isOpen
              gallery={gallery}
              initialIndex={lightboxIndex}
              onBack={isPreviewOpen ? returnToGalleryPreview : undefined}
              onClose={closeGallery}
              onDownloaded={() =>
                setSnackbarMessage(t('home.newsDetail.imageDownloadedSnackbar'))
              }
            />
          )}

          {isHeroOpen && news.titleImageId && (
            <GalleryLightbox
              isOpen
              initialIndex={0}
              gallery={{
                id: `hero-${news.titleImageId}`,
                title: news.title,
                images: [
                  {
                    id: news.titleImageId,
                    fileName: news.title,
                    orderIndex: 0,
                  },
                ],
              }}
              onClose={() => setIsHeroOpen(false)}
              onDownloaded={() =>
                setSnackbarMessage(t('home.newsDetail.imageDownloadedSnackbar'))
              }
            />
          )}

          {previewAttachment && (
            <AttachmentPreview
              attachment={previewAttachment}
              onClose={() => setPreviewAttachment(null)}
              onDownloaded={() =>
                setSnackbarMessage(getDownloadedMessage(previewAttachment.name))
              }
            />
          )}

          <Snackbar
            compact
            isOpen={!!snackbarMessage}
            message={snackbarMessage ?? ''}
            onDismiss={() => setSnackbarMessage(null)}
          />

          {otherNews.length > 0 && (
            <section className="flex flex-col gap-4 px-4 pb-6 max-md:bg-background md:px-0 md:pb-0">
              <h2 className="text-callout-16 text-muted-foreground">
                {t('home.newsDetail.otherNewsTitle')}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {otherNews.map((item, index) => (
                  <NewsCard
                    key={`${item.lastModificationDate}-${index}`}
                    item={item}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
};
